<template>
  <v-autocomplete
    ref="autocomplete"
    v-model="selections"
    :items="uniqueItems"
    item-title="title"
    item-value="_uniqueKey"
    autocomplete="off"
    :custom-filter="customFilter"
    :hint="`選択済み: ${uniqueModelValue.length} / ${maxCount > 0 ? maxCount : ids.length}`"
    multiple
    chips
    small-chips
    persistent-hint
    :clearable="!readonly"
    variant="underlined"
    :menu-props="{ maxHeight: 500 }"
    :style="isMobile ? 'width:80%' : 'width: 50%'"
    color="primary"
    @update:model-value="onUpdateModelValue"
  >
    <template v-if="showAllSelect" #prepend-item>
      <v-list-item ripple :disabled="readonly" @click="toggleAllItems">
        <v-icon :color="uniqueModelValue.length > 0 ? 'primary darken-2' : ''">
          {{ checkBoxIcon() }}
        </v-icon>
        全選択
      </v-list-item>
      <v-divider class="mt-2"></v-divider>
    </template>
    <template #item="{ props, item }">
      <v-list-item
        v-bind="getListItemProps(props, item.raw.id)"
        :title="item?.raw.title"
        :disabled="item.raw.isHeader || readonly"
        @click="
          item.raw.id !== undefined && !item.raw.isHeader && !readonly
            ? handleItemClick(item.raw.id)
            : null
        "
      >
        <template #prepend>
          <v-checkbox
            v-if="!item.raw.isHeader"
            :key="`${item.value}_${item.raw.title}`"
            :model-value="item.raw.id !== undefined && uniqueModelValue.includes(item.raw.id)"
            :ripple="false"
            tabindex="-1"
            hide-details
            density="compact"
            color="primary"
            :readonly="readonly"
            @click.stop="
              item.raw.id !== undefined && !readonly ? handleItemClick(item.raw.id) : null
            "
          />
        </template>
      </v-list-item>
    </template>
    <template #chip="{ index }">
      <v-chip
        v-if="index < uniqueChipItems.length && index < chipCount && uniqueChipItems[index]"
        :closable="closableChips"
        @click:close="readonly ? null : handleChipClose(uniqueChipItems[index]?.value)"
      >
        {{ uniqueChipItems[index]?.title }}
      </v-chip>
      <span v-if="index === chipCount && uniqueChipItems.length > chipCount">
        (+{{ uniqueChipItems.length - chipCount }} {{ unit }})
      </span>
    </template>
  </v-autocomplete>
</template>
<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { VAutocomplete } from "vuetify/lib/components/index.mjs";
import type { SelectItem } from "@/helpers/select-items";

export default defineComponent({
  props: {
    modelValue: { type: Array as PropType<number[]>, default: () => [] as number[] },
    items: { type: Array as PropType<Array<SelectItem>>, default: () => [] as SelectItem[] },
    /** 「全選択」チェックボックスを表示するか否か */
    showAllSelect: { type: Boolean, default: false },
    /** 表示する chip 数。これより多いものは省略表示する */
    chipCount: { type: Number, default: 3 },
    /** 最大選択数。0 の場合は無制限 */
    maxCount: { type: Number, default: 0 },
    /** 例えば '店舗' を指定した場合、chip を省略表示した場合に '(+12 店舗)' のように表示するのに用いる。 */
    unit: { type: String, default: "" },
    /** chip に閉じるボタンを表示するか */
    closableChips: { type: Boolean, default: false },
    /** モバイル表示か否か */
    isMobile: { type: Boolean, default: false },
    /** 入力変更無効にする場合(閲覧権限・認証レビュー時などで使用) */
    readonly: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  computed: {
    /** 選択したものの _uniqueKey（v-autocomplete用） */
    selections: {
      get(): string[] {
        // uniqueModelValue（IDの配列）から_uniqueKeyの配列を生成
        const keys: string[] = [];
        const seenIds = new Set<number>();
        for (let i = 0; i < this.uniqueItems.length; i++) {
          const item = this.uniqueItems[i];
          const id = item.id;
          if (id !== undefined && this.uniqueModelValue.includes(id) && !seenIds.has(id)) {
            seenIds.add(id);
            keys.push(item._uniqueKey);
          }
        }
        return keys;
      },
      set(value: string[]) {
        // _uniqueKeyからIDを抽出してemitする
        const ids = value
          .map((key) => {
            const parts = key.split("_");
            return parts.length > 1 ? Number(parts[parts.length - 1]) : null;
          })
          .filter((id): id is number => id !== null && !isNaN(id));
        // 重複を除外してからemitする
        const uniqueValue = Array.from(new Set(ids));
        this.$emit("update:modelValue", uniqueValue);
      },
    },
    /** items の id の重複を除外したもの */
    ids(): number[] {
      const itemKeys = {};
      this.items
        .filter((item) => typeof item.id === "number" || typeof item.id === "bigint")
        .forEach((item) => {
          itemKeys[item.id] = true;
        });
      return Object.keys(itemKeys).map((key) => Number(key));
    },
    /** modelValue の重複を除外したもの */
    uniqueModelValue(): number[] {
      const uniqueIds = new Set(this.modelValue);
      return Array.from(uniqueIds);
    },
    /** items から重複する id を除外したもの（最初に出現したアイテムを保持） */
    uniqueItems(): Array<SelectItem & { _uniqueKey: string }> {
      // 店舗が複数のエリアに属している場合、各エリアの下に表示する必要があるため、
      // 重複を除外せずにすべてのアイテムを追加する
      // v-autocompleteで同じIDのアイテムが複数あるとエラーになるため、
      // 各アイテムに一意のキーを追加する
      return this.items.map((item, index) => ({
        ...item,
        _uniqueKey: `${index}_${item.id ?? "header"}`,
      }));
    },
    /** chip表示用の重複を除外したアイテムリスト */
    uniqueChipItems(): Array<{ title: string; value: number }> {
      const seenIds = new Set<number>();
      const unique: Array<{ title: string; value: number }> = [];
      for (const id of this.uniqueModelValue) {
        if (!seenIds.has(id)) {
          seenIds.add(id);
          const item = this.items.find((i) => i.id === id && !i.isHeader);
          if (item && item.id !== undefined) {
            unique.push({ title: item.title, value: item.id });
          }
        }
      }
      return unique;
    },
  },
  methods: {
    /** 「全選択」チェックボックスのアイコンを返す */
    checkBoxIcon(): string {
      if (this.uniqueModelValue.length === this.ids.length) return "mdi-close-box";
      if (this.uniqueModelValue.length > 0) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
    /** 「全選択」チェックボックスを押したときの動作 */
    toggleAllItems(): void {
      if (this.uniqueModelValue.length === this.ids.length) {
        this.$emit("update:modelValue", []);
      } else {
        this.$emit("update:modelValue", this.ids);
      }
    },
    validate(): void {
      (this.$refs.autocomplete as typeof VAutocomplete).validate();
    },
    customFilter(value: string, query: string, item?: { raw: SelectItem }): boolean {
      const keyword = item?.raw?.keyword ?? "";
      if (keyword === "") {
        return value.toLowerCase().includes(query.toLowerCase());
      }
      return keyword.toLowerCase().includes(query.toLowerCase());
    },
    onUpdateModelValue(newSelections: string[]): void {
      // _uniqueKeyからIDを抽出する（形式: "index_id"）
      const ids = newSelections
        .map((key) => {
          const parts = key.split("_");
          return parts.length > 1 ? Number(parts[parts.length - 1]) : null;
        })
        .filter((id): id is number => id !== null && !isNaN(id));
      // 重複を除外する
      let uniqueSelections = Array.from(new Set(ids));
      // 最大選択数が指定されていた場合は制限する
      if (this.maxCount > 0 && uniqueSelections.length > this.maxCount) {
        uniqueSelections = uniqueSelections.slice(0, this.maxCount);
      }
      // 直接emitする（selectionsのsetterを経由しない）
      this.$emit("update:modelValue", uniqueSelections);
    },
    /** アイテムのクリックを処理する（チェックボックスのon/offを手動で管理） */
    handleItemClick(itemId: number | undefined): void {
      if (itemId === undefined || this.readonly) {
        return;
      }
      const currentSelections = [...this.uniqueModelValue];
      const index = currentSelections.indexOf(itemId);
      if (index > -1) {
        // 既に選択されている場合は削除
        currentSelections.splice(index, 1);
      } else {
        // 選択されていない場合は追加
        // 最大選択数が指定されていた場合は制限する
        if (this.maxCount === 0 || currentSelections.length < this.maxCount) {
          currentSelections.push(itemId);
        } else {
          return;
        }
      }
      this.$emit("update:modelValue", currentSelections);
    },
    /** chipの閉じるボタンをクリックしたときの処理 */
    handleChipClose(itemId: number | undefined): void {
      if (itemId === undefined || this.readonly) {
        return;
      }
      const currentSelections = this.uniqueModelValue.filter((id) => id !== itemId);
      this.$emit("update:modelValue", currentSelections);
    },
    /** アイテムが選択されているかどうかをIDベースで判定する */
    isItemSelected(itemId: number | undefined): boolean {
      return itemId !== undefined && this.uniqueModelValue.includes(itemId);
    },
    /** v-list-itemに渡すpropsを生成する（selectedとactiveをIDベースで上書き） */
    getListItemProps(props: any, itemId: number | undefined): any {
      const isSelected = this.isItemSelected(itemId);
      // propsからselectedとactiveを除外してから、新しい値を設定
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selected: _selected, active: _active, ...restProps } = props;
      return {
        ...restProps,
        selected: isSelected,
        active: isSelected,
      };
    },
  },
});
</script>
